import React, { useState } from 'react';
import { signIn, signUp, signInWithSso, sendPasswordReset } from '../AuthContext';
import { isBackendConfigured } from '../utils/backendApi';
import Clickwrap from './Clickwrap';
import OAuthButtons from './OAuthButtons';
import { acceptLegal, fetchAgreements } from '../data/legal';
import { discoverSignup } from '../data/entities';

export default function Login() {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState(null); // { providerId, kind, enforced, orgName } for the typed email's domain
  const [company, setCompany] = useState(null); // { id, name } — an org that has verified this domain
  const [docs, setDocs] = useState(null); // signup clickwrap, once the form is valid

  // Discovery by email domain (public, GET /signup/discover): an org that has
  // PROVEN the domain may offer or require "Continue with SSO", and a signup
  // from that domain is told the company is already here (they request to
  // join after signing in instead of creating a second organization).
  const discover = async (value) => {
    const domain = String(value || '').split('@')[1];
    if (!domain || !domain.includes('.') || !isBackendConfigured()) { setSso(null); setCompany(null); return; }
    try {
      const data = await discoverSignup(value);
      setSso(data?.sso || null);
      setCompany(data?.org || null);
    } catch { setSso(null); setCompany(null); }
  };
  const continueSso = async () => {
    setError(null); setBusy(true);
    try { await signInWithSso(sso.providerId); } catch (err) { setError(err.message); setBusy(false); }
  };

  const reviewAgreements = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const list = await fetchAgreements();
      if (!list.length) throw new Error('Agreements are not available right now.');
      setDocs(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finishSignup = async (seen) => {
    setError(null);
    setBusy(true);
    const docIds = seen.map((d) => d.id);
    let created = false;
    try {
      // /login redirects the moment the account exists, which can unmount
      // this form before the acceptance write returns. The console gate
      // finishes a pending write left in sessionStorage.
      try { sessionStorage.setItem('nv.clickwrapUser', JSON.stringify(docIds)); } catch { /* ignore */ }
      await signUp(email, password);
      created = true;
      await acceptLegal({ scope: 'user', docIds });
      try { sessionStorage.removeItem('nv.clickwrapUser'); } catch { /* ignore */ }
    } catch (err) {
      if (!created) {
        try { sessionStorage.removeItem('nv.clickwrapUser'); } catch { /* ignore */ }
      }
      setError(err.message);
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await sendPasswordReset(email);
        setNotice(`If an account exists for ${email}, a reset link is on its way. Check your inbox (and spam).`);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cs-modal" style={{ maxWidth: docs ? 960 : 400 }}>
      <div className="cs-brand" style={{ marginBottom: 6 }}>
        <img
          src="/logo.png"
          alt={process.env.REACT_APP_APP_NAME || 'nventr.io'}
          className="cs-brand-logo"
          style={{ height: 32 }}
        />
      </div>
      <h2 style={{ fontSize: 22, marginTop: 0 }}>
        {docs ? 'Agreements' : mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Reset your password' : 'Sign in'}
      </h2>

      {error && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14 }}>
          {notice}
        </div>
      )}

      {docs && (
        <Clickwrap
          docs={docs}
          busy={busy}
          intro="Read to the end, then agree: the terms, privacy policy, and end user agreement."
          onAgree={finishSignup}
        />
      )}

      {!docs && mode !== 'forgot' && !sso?.enforced && (
        <OAuthButtons onError={setError} disabled={busy} />
      )}

      {!docs && (
      <form onSubmit={mode === 'signup' ? reviewAgreements : submit}>
        <div className="cs-field">
          <label>Email</label>
          <input
            type="email"
            className="cs-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={(e) => discover(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        {sso && (
          <button type="button" className="cs-btn cs-btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={continueSso} disabled={busy}>
            {busy ? 'Redirecting…' : `Continue with SSO${sso.orgName ? ` · ${sso.orgName}` : ''}`}
          </button>
        )}
        {sso?.enforced && mode === 'signin' && (
          <p className="cs-help" style={{ marginBottom: 12 }}>Your organization requires single sign-on for this email domain.</p>
        )}
        {company && mode === 'signup' && !sso && (
          <p className="cs-help" style={{ marginBottom: 12 }}>
            <strong style={{ color: 'var(--text)' }}>{company.name}</strong> already uses nventr for this email domain.
            Create your account and you can ask to join it — an administrator approves you.
          </p>
        )}
        {!(sso?.enforced && mode === 'signin') && mode !== 'forgot' && (
        <div className="cs-field">
          <label>Password</label>
          <input
            type="password"
            className="cs-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
        </div>
        )}
        {!(sso?.enforced && mode === 'signin') && (
        <button type="submit" className="cs-btn cs-btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Please wait…' : mode === 'signup' ? 'Review agreements' : mode === 'forgot' ? 'Email me a reset link' : 'Sign in'}
        </button>
        )}
      </form>
      )}

      {!docs && (
      <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {mode === 'signin' && !sso?.enforced && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}>
            Forgot password?
          </button>
        )}
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setDocs(null); setError(null); setNotice(null); }}
        >
          {mode === 'signin' ? 'Need an account? Create one' : 'Back to sign in'}
        </button>
      </div>
      )}

      <p style={{ textAlign: 'center', marginTop: 14, fontSize: 12.5, color: 'var(--muted)' }}>
        New accounts verify their email first. With a company address you
        join your organization (an admin approves) or start one; a personal
        address starts its own.
      </p>
    </div>
  );
}
